import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { vocabLearningAPI, languagesAPI } from '../services/api';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Fade,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  School as SchoolIcon,
  Home as HomeIcon,
  MenuBook as MenuBookIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { DeckCard, DeckCardSkeleton, ResponsiveGrid } from '../components/DeckCard/DeckCard';

const toNumberOrNull = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const deckMatchesLanguageId = (deck, selectedLanguageId) => {
  if (!selectedLanguageId) return true;

  const deckId = toNumberOrNull(deck?.languageId ?? deck?.language_id ?? deck?.language?.id);
  return Boolean(deckId && deckId === selectedLanguageId);
};

export default function VocabLearning() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [majors, setMajors] = useState([
    { value: 'all', label: 'All Decks' },
  ]);
  const [decks, setDecks] = useState([]);
  const [filteredDecks, setFilteredDecks] = useState([]);
  const [loadingDecks, setLoadingDecks] = useState(true);
  const [selectedMajor, setSelectedMajor] = useState('all');

  // Fetch languages on mount
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await languagesAPI.getAll();
        if (response.success && Array.isArray(response.data)) {
          const dynamicMajors = response.data
            .map((lang) => ({
              value: String(lang.languageId ?? lang.id ?? '').trim(),
              label: lang.name,
              id: lang.id,
              languageId: lang.languageId ?? lang.id,
            }))
            .filter((item) => item.value);
          setMajors([
            { value: 'all', label: 'All Decks' },
            ...dynamicMajors
          ]);
        }
      } catch (error) {
        console.error('Failed to fetch languages:', error);
      }
    };
    fetchLanguages();
  }, []);

  useEffect(() => {
    if (!authLoading) {
      loadDecks();
    }
  }, [authLoading, user]);

  useEffect(() => {
    let result = [...decks];
    if (selectedMajor !== 'all') {
      const selectedLanguageId = toNumberOrNull(selectedMajor);
      result = result.filter((deck) => deckMatchesLanguageId(deck, selectedLanguageId));
    }
    setFilteredDecks(result);
  }, [decks, selectedMajor, majors]);

  const loadDecks = async () => {
    try {
      setLoadingDecks(true);
      const response = await vocabLearningAPI.getDecks(null, null, user?.id);
      // New API response shape: { success: true, data: [...], meta: {...} }
      setDecks(response.data || response.decks || []);
    } catch (error) {
      console.error('Failed to load decks:', error);
      setDecks([]);
    } finally {
      setLoadingDecks(false);
    }
  };

  const handleMajorChange = (event, newValue) => {
    setSelectedMajor(newValue);
  };

  const handleDeckClick = (deck) => {
    navigate(`/vocab-learning/learn/${deck.id}`, { state: { deck } });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="xl" sx={{ py: 3, px: { xs: 2, sm: 3, md: 4 } }}>
        <Breadcrumbs
          sx={{ mb: 3 }}
          separator={<ChevronRightIcon sx={{ fontSize: 14, opacity: 0.5 }} />}
        >
          <Link
            component={RouterLink}
            to="/"
            underline="hover"
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'text.secondary',
              fontSize: '0.8125rem',
              fontWeight: 500,
              '&:hover': { color: 'primary.main' },
            }}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: 16 }} />
            Home
          </Link>
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'text.primary',
              fontSize: '0.8125rem',
              fontWeight: 600,
            }}
          >
            <MenuBookIcon sx={{ mr: 0.5, fontSize: 16 }} />
            Vocab Learning
          </Typography>
        </Breadcrumbs>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-1px', mb: 0.5, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            Vocab Learning
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Practice vocabulary with spaced repetition flashcards. Choose a deck to start.
          </Typography>
        </Box>

        <Box sx={{ mb: 4, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tabs
            value={selectedMajor}
            onChange={handleMajorChange}
            sx={{
              minHeight: 40,
              '& .MuiTabs-indicator': { height: 2.5, borderRadius: '2px 2px 0 0' },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
                minHeight: 40,
                px: 2.5,
                color: 'text.secondary',
                '&.Mui-selected': { color: 'primary.main' },
              },
            }}
          >
            {majors.map((major) => (
              <Tab key={major.value} label={major.label} value={major.value} />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {filteredDecks.length} {filteredDecks.length === 1 ? 'deck' : 'decks'} available
          </Typography>
        </Box>

        {loadingDecks ? (
          <ResponsiveGrid>
            {[1, 2, 3, 4].map((i) => (
              <DeckCardSkeleton key={i} />
            ))}
          </ResponsiveGrid>
        ) : filteredDecks.length > 0 ? (
          <Fade in timeout={500}>
            <Box>
              <ResponsiveGrid>
                {filteredDecks.map((deck) => (
                  <DeckCard
                    key={deck.id}
                    deck={deck}
                    languageName={deck.languageName || deck.language?.name || deck.major || deck.languageCode}
                    onClick={handleDeckClick}
                  />
                ))}
              </ResponsiveGrid>
            </Box>
          </Fade>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
            <SchoolIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
            <Typography variant="h6" gutterBottom>No decks available</Typography>
            <Typography variant="body2">
              {selectedMajor === 'all'
                ? 'There are no vocabulary decks available at the moment.'
                : `No ${majors.find((m) => m.value === selectedMajor)?.label.toLowerCase()} decks available.`}
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
}
